// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useSavingGoalsStore } from '@/store/savingGoalsStore'
import { useCurrenciesStore } from '@/store/currenciesStore'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
// Migrated: Using native Date instead of Firebase Timestamp
import { CalendarIcon, Target, Calculator, AlertCircle, Building2, X } from 'lucide-react'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { format, addMonths } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency } from '@/utils/formatters'
import { AccountType } from '@/types/account'

interface SimplifiedAccount {
  id: string;
  name: string;
  type: AccountType;
  balance: number;
  currency?: string;
}

interface AddSavingGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savingsAccounts: SimplifiedAccount[];
}

const GOAL_CATEGORIES = [
  'Emergency Fund',
  'Travel',
  'Education',
  'Home',
  'Car',
  'Wedding',
  'Retirement',
  'Other'
] as const

export function AddSavingGoalDialog({ open, onOpenChange, savingsAccounts }: AddSavingGoalDialogProps) {
  const { user } = useAuth()
  const { addGoal } = useSavingGoalsStore()
  const { currencies } = useCurrenciesStore()
  const currencySymbol = currencies.find(c => c.isDefault)?.symbol || '$'
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    targetAmount: '',
    category: '',
    description: '',
    deadline: addMonths(new Date(), 1),
    selectedAccountId: ''
  })

  const [validationError, setValidationError] = useState<string | null>(null)

  // Debug logging for savingsAccounts
  useEffect(() => {
    console.log('Savings accounts in dialog:', savingsAccounts);
  }, [savingsAccounts]);

  const suggestedSavings = formData.targetAmount
    ? {
        daily: parseFloat(formData.targetAmount) / 30,
        weekly: parseFloat(formData.targetAmount) / 4,
        monthly: parseFloat(formData.targetAmount)
      }
    : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    try {
      setValidationError(null)
      setLoading(true)
      const amount = parseFloat(formData.targetAmount)
      
      if (!formData.name.trim()) {
        throw new Error('Please enter a goal name')
      }
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid target amount')
      }
      if (!formData.category) {
        throw new Error('Please select a category')
      }
      if (formData.deadline < new Date()) {
        throw new Error('Please select a future date')
      }
      if (!formData.selectedAccountId) {
        throw new Error('Please select a savings account')
      }

      const selectedAccount = savingsAccounts.find(acc => acc.id === formData.selectedAccountId)
      
      if (!selectedAccount) {
        throw new Error('Selected account not found')
      }

      await addGoal({
        name: formData.name.trim(),
        target_amount: amount,
        current_amount: 0,
        currency: selectedAccount.currency || 'USD',
        target_date: formData.deadline.toISOString(),
        description: formData.description.trim() || null,
        account_id: formData.selectedAccountId,
        icon: null,
        color: null,
        status: 'active'
      })

      toast.success('Saving goal created successfully!')
      
      requestAnimationFrame(() => {
        console.log('Closing add goal dialog with state reset');
        onOpenChange(false)
        setFormData({
          name: '',
          targetAmount: '',
          category: '',
          description: '',
          deadline: addMonths(new Date(), 1),
          selectedAccountId: ''
        })
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create saving goal'
      setValidationError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) {
        setFormData({
          name: '',
          targetAmount: '',
          category: '',
          description: '',
          deadline: addMonths(new Date(), 1),
          selectedAccountId: ''
        })
      }
      onOpenChange(newOpen)
    }}>
      <DialogContent 
        className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl sm:max-w-[600px]"
        onPointerDownOutside={(e) => {
          if (!loading) {
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!loading) {
            e.preventDefault()
          }
        }}
      >
        <DialogClose 
          asChild
          disabled={loading}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-sm text-text-secondary dark:text-text-dark-secondary opacity-70 hover:opacity-100 focus:outline-none disabled:pointer-events-none"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (!loading) {
                onOpenChange(false)
              }
            }}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogClose>

        <DialogHeader className="space-y-2">
          <DialogTitle className="text-text-primary dark:text-text-dark-primary flex items-center text-2xl">
            <Target className="w-6 h-6 mr-2 text-secondary dark:text-primary-dark" />
            Create New Saving Goal
          </DialogTitle>
          <DialogDescription className="text-text-secondary dark:text-text-dark-secondary">
            Set up a new financial goal and track your progress towards achieving it.
          </DialogDescription>
        </DialogHeader>

        {validationError && (
          <Alert variant="destructive" className="border-negative/20 dark:border-negative/30 bg-negative/10 dark:bg-negative-dark/10">
            <AlertCircle className="h-4 w-4 text-negative dark:text-negative-dark" />
            <AlertDescription className="text-negative dark:text-negative-dark">{validationError}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-text-secondary dark:text-text-dark-secondary">Goal Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., New Car Fund"
                className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-text-secondary dark:text-text-dark-secondary">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger 
                  id="category"
                  className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark rounded-xl">
                  {GOAL_CATEGORIES.map((category) => (
                    <SelectItem 
                      key={category} 
                      value={category}
                      className="text-text-primary dark:text-text-dark-primary focus:bg-secondary/10 dark:focus:bg-primary-dark/10 rounded-lg"
                    >
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="targetAmount" className="text-text-secondary dark:text-text-dark-secondary">Target Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary">
                  {currencySymbol}
                </span>
                <Input
                  id="targetAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.targetAmount}
                  onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
                  className="pl-7 bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deadline" className="text-text-secondary dark:text-text-dark-secondary">Target Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="deadline"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark hover:bg-background-input dark:hover:bg-background-dark-input focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11",
                      !formData.deadline && "text-text-secondary dark:text-text-dark-secondary"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.deadline ? format(formData.deadline, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark rounded-xl">
                  <Calendar
                    mode="single"
                    selected={formData.deadline}
                    onSelect={(date) => date && setFormData({ ...formData, deadline: date })}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-text-secondary dark:text-text-dark-secondary">Description (Optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add more details about your goal..."
              className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="account" className="text-text-secondary dark:text-text-dark-secondary">Savings Account</Label>
            {savingsAccounts.length === 0 ? (
              <Alert className="border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 rounded-xl">
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  No savings accounts available. Please create a savings account first.
                </p>
              </Alert>
            ) : (
              <Select
                value={formData.selectedAccountId}
                onValueChange={(value) => setFormData({ ...formData, selectedAccountId: value })}
              >
                <SelectTrigger 
                  id="account" 
                  className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                >
                  <SelectValue placeholder="Select a savings account" />
                </SelectTrigger>
                <SelectContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark rounded-xl">
                  {savingsAccounts.map((account) => (
                    <SelectItem 
                      key={account.id} 
                      value={account.id}
                      className="text-text-primary dark:text-text-dark-primary focus:bg-secondary/10 dark:focus:bg-primary-dark/10 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-secondary dark:text-primary-dark" />
                        <span>{account.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {suggestedSavings && (
            <Card className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-secondary dark:text-primary-dark">
                  <Calculator className="w-4 h-4" />
                  <span className="font-medium">Suggested Savings</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Daily</p>
                    <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                      {currencySymbol}{formatCurrency(suggestedSavings.daily, currencies.find(c => c.isDefault))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Weekly</p>
                    <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                      {currencySymbol}{formatCurrency(suggestedSavings.weekly, currencies.find(c => c.isDefault))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Monthly</p>
                    <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                      {currencySymbol}{formatCurrency(suggestedSavings.monthly, currencies.find(c => c.isDefault))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl h-11"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || savingsAccounts.length === 0}
              className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
            >
              {loading ? 'Creating...' : 'Create Goal'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
